import { logger } from '@tunasong/models'
import { MAJOR_TONICS, MINOR_TONICS } from '@tunasong/music-lib'
import type { NoteName, Scale } from '@tunasong/schemas'

export type QuizKeyType = 'major' | 'minor'

/**
 * Parse the `?keyType=` URL param. Case-insensitive. Returns `null` on
 * missing or invalid — Function Quiz reads "no preference, keep current
 * per-question randomisation."
 *
 * Spec: docs/superpowers/specs/2026-05-20-tool-interlinking-design.md §6
 */
export const parseKeyTypeParam = (raw: string | null): QuizKeyType | null => {
  if (raw === null || raw === '') {
    return null
  }
  const normalised = raw.toLowerCase()
  if (normalised === 'major' || normalised === 'minor') {
    return normalised
  }
  return null
}

/**
 * Parse the `?key=` URL param against the locked tonic table for the
 * provided `keyType`. Case-sensitive — accidentals matter: `Db` is in
 * `MAJOR_TONICS`, `C#` is in `MINOR_TONICS`; the spellings are not
 * interchangeable per Bowie's locked tables in `function-quiz.ts`.
 *
 * Spec: docs/superpowers/specs/2026-05-20-tool-interlinking-design.md §6
 */
export const parseKeyParam = (raw: string | null, keyType: QuizKeyType): NoteName | null => {
  if (raw === null || raw === '') {
    return null
  }
  const pool = keyType === 'major' ? MAJOR_TONICS : MINOR_TONICS
  return (pool as readonly NoteName[]).includes(raw as NoteName) ? (raw as NoteName) : null
}

/**
 * Combined parser. Returns a `Scale` only when BOTH `?key` and `?keyType`
 * are valid AND the key spelling matches the keyType's pool. Logs a warn
 * when the params are present but malformed, so misuse is visible in the
 * console.
 *
 * Spec: docs/superpowers/specs/2026-05-20-tool-interlinking-design.md §6
 */
export const parseForcedScale = (rawKey: string | null, rawKeyType: string | null): Scale | null => {
  const keyType = parseKeyTypeParam(rawKeyType)
  if (keyType === null) {
    if (rawKey !== null || rawKeyType !== null) {
      logger.warn('Function Quiz: incomplete or invalid keyType URL param', { rawKey, rawKeyType })
    }
    return null
  }
  const key = parseKeyParam(rawKey, keyType)
  if (key === null) {
    logger.warn('Function Quiz: invalid key URL param for given keyType', { rawKey, keyType })
    return null
  }
  return { root: key, type: keyType }
}
